import {
  PRICE_CHANGE_NOTE_MAX,
  clampPriceChangeNote,
  isPriceChangeCents,
  isPriceChangeReason,
  type PriceChangePayload,
  type PriceChangeReason,
} from '@barso/shared';
import type { CartLine, LinePriceChange } from './types';

/**
 * Preis an der Kasse korrigieren (U17g, Plan 16 Abschnitt 4.10.8). Reine Funktionen auf dem Warenkorb, alles in ganzen Cent
 * (Regel 3). Die Summe rechnet weiter `cartTotal` aus `priceCents`: Es gibt keinen zweiten Rechenweg.
 *
 * Läuft eine Aktion (X7), ersetzt die Korrektur den Aktionspreis: Die Zeile verliert `promo`, und `promoId` wird nicht gesendet.
 * Beides zugleich gibt es nicht.
 */

/** Grund fehlt oder „Anderer Grund“ ohne Text: Der Preis gilt dann nicht (die Oberfläche lässt es gar nicht zu). */
export type PriceChangeError = 'price' | 'unchanged' | 'reason' | 'note';

export function validatePriceChange(
  line: CartLine,
  newCents: number,
  reason: PriceChangeReason | null,
  note: string | undefined,
): PriceChangeError | null {
  if (!isPriceChangeCents(newCents)) return 'price';
  if (newCents === line.priceCents) return 'unchanged';
  if (reason === null || !isPriceChangeReason(reason)) return 'reason';
  if (reason === 'other' && clampPriceChangeNote(note) === undefined) return 'note';
  return null;
}

/**
 * Setzt den korrigierten Preis. `originalCents` bleibt bei einer zweiten Korrektur der Preis vor der ERSTEN (die Leitung sieht,
 * was der Artikel kostete, nicht einen Zwischenstand). Gibt `null` zurück, wenn die Angaben nicht gelten (kein Grund, Text
 * fehlt, Preis außerhalb, unverändert).
 */
export function applyPriceChange(
  line: CartLine,
  newCents: number,
  reason: PriceChangeReason | null,
  note?: string,
): CartLine | null {
  if (validatePriceChange(line, newCents, reason, note) !== null || reason === null) return null;
  const text = clampPriceChangeNote(note);
  const change: LinePriceChange = {
    originalCents: line.priceChange?.originalCents ?? line.priceCents,
    reason,
    ...(text === undefined ? {} : { note: text }),
    // Die Aktion der Zeile wird für das Zurücknehmen gemerkt, bleibt aber nicht an der Zeile hängen
    wasPromo: line.priceChange === undefined ? (line.promo ?? null) : line.priceChange.wasPromo,
  };
  return { ...line, priceCents: newCents, promo: null, priceChange: change };
}

/** Nimmt die Korrektur zurück: Preis und Aktion wie vor der Korrektur. */
export function clearPriceChange(line: CartLine): CartLine {
  if (line.priceChange === undefined) return line;
  const { priceChange, ...rest } = line;
  return {
    ...rest,
    priceCents: priceChange.originalCents,
    promo: priceChange.wasPromo ?? null,
  };
}

/** Angabe für die Verkaufszeile im Protokoll, nur mit gemeldeter Fähigkeit des Servers. */
export function priceChangePayload(
  line: CartLine,
  allowed: readonly PriceChangeReason[] | undefined,
): PriceChangePayload | undefined {
  const change = line.priceChange;
  if (change === undefined || allowed?.includes(change.reason) !== true) return undefined;
  return {
    originalCents: change.originalCents,
    reason: change.reason,
    ...(change.note === undefined ? {} : { note: change.note.slice(0, PRICE_CHANGE_NOTE_MAX) }),
  };
}

/** Gemerkte Korrektur einer Zeile beim Neuaufbau des Warenkorbs (Korrigieren, Zurückholen): Preis und Angaben. */
export interface KeptPriceChange {
  priceCents: number;
  priceChange: LinePriceChange;
}

/** Merkt die Korrektur einer Zeile (nur Zeilen mit Artikel und Korrektur), sonst `undefined`. */
export function keepPriceChange(line: CartLine): KeptPriceChange | undefined {
  if (line.priceChange === undefined || line.itemId === null) return undefined;
  return { priceCents: line.priceCents, priceChange: line.priceChange };
}

/**
 * Setzt eine gemerkte Korrektur wieder an die frisch geprüfte Zeile desselben Artikels (Korrigieren, K5): Preis, Grund und Text
 * bleiben, eine Aktion gilt dann nicht (Korrektur und Aktion gibt es nie zugleich). Andere Artikel bleiben unberührt.
 */
export function reapplyPriceChange(line: CartLine, kept: KeptPriceChange | undefined): CartLine {
  if (kept === undefined || line.itemId === null) return line;
  return { ...line, priceCents: kept.priceCents, promo: null, priceChange: kept.priceChange };
}
